/**
 * A record's head, in the designer: its settings in the panel (Record head),
 * its buttons', tabs' and fields' own parts, what the canvas says of it, and
 * why an edit of it is refused.
 */
export const head = {
  // ---- the head's first buttons, as a work item has them
  starter: { saveClose: 'Save and Close', save: 'Save', refresh: 'Refresh', undo: 'Revert changes' },
  // ---- the sheet's settings: Record head
  recordHead: 'Record head',
  recordHeadHint: 'A work item’s head over the record: its kind’s strip and badge, the title line, a row of people and buttons, a band of fields and the tabs.',
  headOn: 'A head over the record',
  kindFrom: 'Kind from',
  oneKind: 'One kind',
  kindWords: 'Kind’s words',
  kindWordsHint: 'With one kind, its name in the badge: “Bug”.',
  numberFrom: 'Number from',
  recordsId: 'The record’s id',
  kindColour: 'Kind’s colour',
  kindIcon: 'Kind’s icon',
  noIcon: 'No icon',
  kindLooks: 'Each kind’s look',
  lookOf: (kind: string) => `${kind}: colour and icon`,
  colourOf: (kind: string) => `${kind}’s colour`,
  iconOf: (kind: string) => `${kind}’s icon`,
  copyTitle: 'Copy title button',
  bandColumns: 'Band’s columns',
  bandColumnsHint: 'Their widths, apart by spaces: “274px 1fr”. Two even columns when empty.',
  labelWidth: 'Band’s labels’ width',
  bandFields: 'Band’s fields',
  addBandField: 'Add a field to the band',
  rowParts: 'Row under the title',
  addRowField: 'Add a field to the row',
  addRowLink: 'Add a count to the row',
  linkWordsOne: (field: string) => `${field}: words after one`,
  linkWordsOther: (field: string) => `${field}: words after more`,
  linkTarget: (field: string) => `${field}: takes the reader to`,
  nowhere: 'Nowhere',
  actions: 'Buttons',
  addButton: 'Add a button',
  newButton: 'New button',
  buttonHint: 'Pick a button on the canvas to set what it does and how it looks.',
  updatedBy: 'Updated by',
  updatedAt: 'Updated on',
  updatedOpens: 'Pressed, “Updated by” shows',
  noUpdated: 'Not shown',
  noTab: 'No tab',
  accessKey: 'Access key, with Alt',
  accessKeyHint: 'A letter of its label: underlined in it, and Alt with it takes the focus to the field.',
  accessKeyOf: (field: string) => `${field}: access key`,
  removeNamed: (name: string) => `Remove ${name}`,
  moveEarlier: (name: string) => `Move ${name} earlier`,
  moveLater: (name: string) => `Move ${name} later`,
  pick: 'Pick…',
  // ---- a button's own parts
  iconOnly: 'Its icon alone',
  icon: 'Icon',
  toggle: 'Pressed while',
  notToggle: 'Not a toggle',
  pressedWords: 'Words while pressed',
  primaryWhenChanged: 'Primary while the record has changes',
  menu: 'Menu beside it',
  menuHint: 'Each choice’s words and the app’s action; a button with no steps or action of its own opens it.',
  addChoice: 'Add a choice',
  addDivider: 'Add a divider',
  removeDivider: 'Remove the divider',
  choiceWords: 'Choice’s words',
  choiceAction: 'Its action',
  newChoice: 'New choice',
  chevronIcon: 'Chevron’s icon',
  chevronName: 'Chevron’s name',
  // ---- a tab's own parts, and tabs in the head
  tabIcon: 'Icon',
  tabIconOnly: 'Its icon alone',
  countFrom: 'Count from',
  noCount: 'No count',
  countRelated: (name: string) => `Related list: ${name}`,
  inHead: 'In the record’s head',
  inHeadHint: 'The tabs at the band’s end, their pages as the record’s body.',
  // ---- the canvas
  canvasHead: 'The record’s head: pick it to set it',
  // ---- why an edit is refused
  onlySheet: 'Only a record sheet has a head',
  noHead: 'This sheet has no head yet',
  noPart: (id: string) => `The head has no part "${id}"`,
  noButton: (id: string) => `There is no button "${id}"`,
  noTabNamed: (id: string) => `There is no tab "${id}"`,
  kindOneOfList: (label: string) => `${label} holds no kinds: a kind is one of a list, or a link`,
  numberShape: (label: string) => `${label} cannot be a record’s number: a number or words can`,
  colourShape: 'A colour is written #rrggbb, as #cc293d',
  tracksShape: 'Columns are widths such as 274px, 50% or 1fr, one to four of them',
  labelWidthShape: 'Labels are 48 to 320 pixels wide',
  datesOnly: (label: string) => `${label} is no date: when it was updated is a date`,
  looksNeedField: 'Each kind’s look needs the field that holds the kind',
  linksInRow: 'A count goes in the row under the title',
  linkNeedsNumber: (label: string) => `${label} is no number: a count’s words follow a number`,
  linkNeedsWords: 'A count needs its words',
  iconOnlyNeedsIcon: 'Shown by its icon alone, it needs an icon',
  toggleYesNo: (label: string) => `${label} is not yes or no: a toggle is pressed while a yes/no field is yes`,
  pressedWordsNeedToggle: 'Words while pressed need what presses it',
  chevronNeedsMenu: 'A chevron of its own needs a menu to open',
  countShape: (label: string) => `${label} cannot be counted: a tab counts a number, lines, links or files`,
  countNoRelated: (id: string) => `There is no related list "${id}" in this tab: a tab counts the records of one it holds`,
  tabsDirectlyInHead: 'Tabs go in the head from directly in a sheet with a head',
  accessKeyShape: 'An access key is one letter or digit of the label',
};
